import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useEventById } from '@/hooks/useEvents';
import ImageUpload from '@/components/admin/ImageUpload';
import StatusToggle from '@/components/admin/StatusToggle';
import { formatYmdInBrussels, brusselsInstantFromYmdAndHm, formatHmInBrussels, todayYmdBrussels } from '@/lib/brusselsSchedule';
import { UTA_EVENT_START_HM, UTA_EVENT_END_HM, normalizeEndTimeToHm } from '@/lib/eventTimes';

const DEFAULT_LOCATION = 'Parc de Juliénas 1, 6940 Barvaux-sur-Ourthe (Durbuy)';

const CIVILITY_OPTIONS = ['Monsieur', 'Madame', 'Monsieur et Madame'];

function slugify(input) {
    return String(input ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

/**
 * input type=time : Chrome « 14:30 », d’autres « 14:30:00 » ou « 9:30 » — on normalise en HH:mm.
 */
function normalizeTimeInputToHm(s, fallbackHm) {
    const raw = String(s ?? '').trim();
    const m = /^(\d{1,2}):(\d{2})(?::\d{2})?/.exec(raw);
    if (!m) return fallbackHm;
    return `${m[1].padStart(2, '0')}:${m[2]}`;
}

/** Normalise les champs au moment de la soumission : un clic « Enregistrer » doit suffire. */
function preprocessEventForm(raw) {
    if (!raw || typeof raw !== 'object') return raw;
    const title = String(raw.title ?? '').trim();
    let slug = String(raw.slug ?? '').trim();
    if (slug.length < 3) {
        slug = slugify(title);
    }
    if (slug.length < 1) {
        slug = 'evenement';
    }
    return {
        ...raw,
        start_time: normalizeTimeInputToHm(raw.start_time, UTA_EVENT_START_HM),
        end_time_hm: normalizeTimeInputToHm(raw.end_time_hm, UTA_EVENT_END_HM),
        title,
        slug,
        location: String(raw.location ?? '').trim() || DEFAULT_LOCATION,
        image_url: raw.image_url === '' || raw.image_url == null ? null : raw.image_url,
        speaker_civility:
            raw.speaker_civility === '' || raw.speaker_civility == null
                ? null
                : CIVILITY_OPTIONS.includes(raw.speaker_civility)
                  ? raw.speaker_civility
                  : null,
        speaker_name: raw.speaker_name?.toString().trim() || null,
        speaker_title: raw.speaker_title?.toString().trim() || null,
        speaker_description: raw.speaker_description?.toString().trim() || null,
        description: raw.description?.toString().trim() || null,
    };
}

const schema = z.preprocess(
    preprocessEventForm,
    z.object({
        title: z.string().min(1, 'Le titre est obligatoire'),
        slug: z.string().min(1, 'Slug manquant'),
        type: z.enum(['Conférence', 'Excursion', 'Goûter']),
        speaker_civility: z
            .union([
                z.literal(''),
                z.enum(['Monsieur', 'Madame', 'Monsieur et Madame']),
                z.null(),
            ])
            .optional()
            .transform((v) => (v === '' || v === undefined ? null : v)),
        speaker_name: z.string().nullable().optional(),
        speaker_title: z.string().nullable().optional(),
        speaker_description: z.string().nullable().optional(),
        event_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Indiquez la date'),
        start_time: z.string().regex(/^\d{2}:\d{2}$/, 'Heure de début invalide'),
        end_time_hm: z.string().regex(/^\d{2}:\d{2}$/, 'Heure de fin invalide'),
        location: z.string().min(1, 'Indiquez le lieu'),
        description: z.string().nullable().optional(),
        is_published: z.boolean(),
        /** URL absolue (https://…) ou chemin site (/images/…) — pas seulement z.url() */
        image_url: z
            .union([z.null(), z.string().url(), z.string().regex(/^\/.+/)])
            .optional(),
    })
);

export default function EventForm() {
    const { id } = useParams();
    const isEditing = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: event, isLoading: isLoadingEvent } = useEventById(id);

    const { register, handleSubmit, control, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(schema),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        criteriaMode: 'all',
        shouldFocusError: false,
        defaultValues: {
            title: '',
            slug: '',
            type: 'Conférence',
            speaker_civility: '',
            speaker_name: '',
            speaker_title: '',
            speaker_description: '',
            event_date: todayYmdBrussels(),
            start_time: UTA_EVENT_START_HM,
            end_time_hm: UTA_EVENT_END_HM,
            location: DEFAULT_LOCATION,
            description: '',
            is_published: true,
            image_url: '',
        }
    });

    // Auto-generate slug from title if creating
    const title = watch('title');
    useEffect(() => {
        if (!isEditing && title) {
            const slug = title
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
            setValue('slug', slug);
        }
    }, [title, isEditing, setValue]);

    useEffect(() => {
        if (event) {
            const { date: _omitDate, end_time: dbEndTime, ...rest } = event;
            reset({
                ...rest,
                speaker_civility: CIVILITY_OPTIONS.includes(event.speaker_civility) ? event.speaker_civility : '',
                event_date: event.date ? formatYmdInBrussels(event.date) : todayYmdBrussels(),
                start_time: event.date ? formatHmInBrussels(event.date) : UTA_EVENT_START_HM,
                end_time_hm: normalizeEndTimeToHm(dbEndTime),
            });
        }
    }, [event, reset]);

    const onSubmit = async (data) => {
        try {
            const startUtc = brusselsInstantFromYmdAndHm(data.event_date, data.start_time);
            if (Number.isNaN(startUtc.getTime())) {
                alert('Date ou heure de début invalide.');
                return;
            }
            const eventData = {
                title: data.title,
                slug: data.slug,
                type: data.type,
                location: data.location,
                date: startUtc.toISOString(),
                end_time: `${data.end_time_hm}:00`,
                description: data.description?.trim() || null,
                is_published: data.is_published,
                image_url: data.image_url?.trim() || null,
                speaker_civility: CIVILITY_OPTIONS.includes(data.speaker_civility) ? data.speaker_civility : null,
                speaker_name: data.speaker_name?.trim() || null,
                speaker_title: data.speaker_title?.trim() || null,
                speaker_description: data.speaker_description?.trim() || null,
            };

            let error;
            if (isEditing) {
                ({ error } = await supabase.from('events').update(eventData).eq('id', id));
            } else {
                ({ error } = await supabase.from('events').insert([eventData]));
            }

            if (error) throw error;

            await queryClient.invalidateQueries({ queryKey: ['events'] });
            navigate('/admin/events');
        } catch (error) {
            console.error('Error saving event:', error);
            alert('Erreur lors de la sauvegarde: ' + error.message);
        }
    };

    const onInvalid = (formErrors) => {
        const collect = (err) => {
            if (!err) return [];
            if (typeof err.message === 'string' && err.message) return [err.message];
            return Object.values(err).flatMap((v) => collect(v));
        };
        const messages = collect(formErrors);
        if (messages.length) {
            alert(`Merci de compléter le formulaire :\n\n• ${messages.join('\n• ')}`);
        }
    };

    if (isEditing && isLoadingEvent) {
        return <div className="p-8 text-center">Chargement...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/admin/events')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-3xl font-serif font-bold text-gray-900">
                    {isEditing ? 'Modifier l\'événement' : 'Nouvel événement'}
                </h1>
            </div>

            <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-8" noValidate>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                            <input
                                {...register('title')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                placeholder="Ex: Conférence Histoire de l'Art"
                            />
                            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
                        </div>

                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                                {...register('type')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            >
                                <option value="Conférence">Conférence</option>
                                <option value="Excursion">Excursion</option>
                                <option value="Goûter">Goûter</option>
                            </select>
                        </div>

                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
                            <input
                                {...register('slug')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                            {errors.slug && <p className="text-red-500 text-xs mt-1">{errors.slug.message}</p>}
                        </div>

                        <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div className="sm:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date de l’événement</label>
                                <input
                                    type="date"
                                    {...register('event_date')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                />
                                {errors.event_date && <p className="text-red-500 text-xs mt-1">{errors.event_date.message}</p>}
                            </div>
                            <div className="sm:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Heure de début</label>
                                <input
                                    type="time"
                                    step={60}
                                    {...register('start_time')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none tabular-nums"
                                />
                                {errors.start_time && <p className="text-red-500 text-xs mt-1">{errors.start_time.message}</p>}
                            </div>
                            <div className="sm:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Heure de fin</label>
                                <input
                                    type="time"
                                    step={60}
                                    {...register('end_time_hm')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none tabular-nums"
                                />
                                {errors.end_time_hm && <p className="text-red-500 text-xs mt-1">{errors.end_time_hm.message}</p>}
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
                            <input
                                {...register('location')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>

                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Civilité (optionnel)</label>
                            <select
                                {...register('speaker_civility')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            >
                                <option value="">—</option>
                                <option value="Monsieur">Monsieur</option>
                                <option value="Madame">Madame</option>
                                <option value="Monsieur et Madame">Monsieur et Madame</option>
                            </select>
                        </div>

                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Intervenant (optionnel)</label>
                            <input
                                {...register('speaker_name')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="Ex: Prof. Durand"
                            />
                        </div>

                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Titre Intervenant (Optionnel)</label>
                            <input
                                {...register('speaker_title')}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="Ex: Historien de l'Art"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description Intervenant (Optionnel)</label>
                            <textarea
                                {...register('speaker_description')}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="Bref résumé du parcours ou de l'expertise de l'intervenant"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                {...register('description')}
                                rows={5}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="col-span-1">
                        <Controller
                            control={control}
                            name="image_url"
                            render={({ field }) => (
                                <ImageUpload value={field.value} onChange={field.onChange} />
                            )}
                        />
                    </div>

                    <div className="col-span-1 space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-900">Publié sur le site</span>
                            <Controller
                                control={control}
                                name="is_published"
                                render={({ field }) => (
                                    <StatusToggle enabled={field.value} onChange={field.onChange} />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/events')}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-bold shadow-lg disabled:opacity-50 transition-colors"
                    >
                        <Save size={20} />
                        {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </form>
        </div>
    );
}
